/**
 * Admin Dashboard View
 *
 * Shows export statistics for firm administrators:
 * - Monthly KPI badges at the top (exported, unexported, deleted hours, active users)
 * - High Exporters card (attorneys with >= threshold export rate)
 * - Low Exporters card (attorneys with < threshold export rate)
 *
 * Both cards share the same timeframe and data source for consistency.
 */

import { useState } from "react";
import { useExportStats, useDashboardMetrics } from "../hooks/useAdminData";
import ExportersCard from "../components/ExportersCard";
import styles from "./AdminViews.module.css";

export default function AdminDashboardView() {
  // Shared timeframe state between both cards
  const [timeframe, setTimeframe] = useState("7days");

  // Fetch export stats (shared between both cards)
  const { attorneys, loading } = useExportStats(timeframe);

  // Fetch dashboard metrics for KPI badges
  const { metrics, loading: metricsLoading } = useDashboardMetrics();

  // Get current month name
  const currentMonth = new Date().toLocaleString("en-US", { month: "long" });

  return (
    <div className={`${styles.adminViewWrapper} flex flex-col h-full`}>
      {/* Title */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground">{currentMonth} metrics at a glance</p>
      </div>

      {/* Spacer pushes content to bottom */}
      <div className="flex-1" />

      {/* KPI Cards - Right-aligned above the exporter cards */}
      <div className="flex flex-wrap justify-end gap-3 mb-4">
        {/* Exported Hours */}
        <div className="relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br from-[#1E4D5A] to-[#203234] p-4 min-w-[150px]">
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <p className="text-xs font-medium text-white/70 mb-1">Exported</p>
          <p className="text-2xl font-bold text-white">
            {metricsLoading ? "-" : `${metrics?.exportedHours?.toFixed(1) ?? "0"}`}
            <span className="text-sm font-normal text-white/70 ml-1">hrs</span>
          </p>
        </div>

        {/* Unexported Hours */}
        <div className="relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br from-[#2D5A6B] to-[#1E4D5A] p-4 min-w-[150px]">
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <p className="text-xs font-medium text-white/70 mb-1">Unexported</p>
          <p className="text-2xl font-bold text-white">
            {metricsLoading ? "-" : `${metrics?.unexportedHours?.toFixed(1) ?? "0"}`}
            <span className="text-sm font-normal text-white/70 ml-1">hrs</span>
          </p>
        </div>

        {/* Deleted Hours */}
        <div className="relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br from-[#318CB8] to-[#1E4D5A] p-4 min-w-[150px]">
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <p className="text-xs font-medium text-white/70 mb-1">Deleted</p>
          <p className="text-2xl font-bold text-white">
            {metricsLoading ? "-" : `${metrics?.deletedHours?.toFixed(1) ?? "0"}`}
            <span className="text-sm font-normal text-white/70 ml-1">hrs</span>
          </p>
        </div>

        {/* Active Users */}
        <div className="relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br from-[#203234] to-[#0f1a1b] p-4 min-w-[150px]">
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <p className="text-xs font-medium text-white/70 mb-1">Active Users</p>
          <p className="text-2xl font-bold text-white">
            {metricsLoading ? "-" : `${metrics?.activeUsers ?? "0"}`}
          </p>
        </div>
      </div>

      {/* Responsive grid: stacked on mobile, side-by-side on xl screens */}
      {/* items-stretch ensures both cards have equal height */}
      <div className="flex flex-col xl:flex-row xl:items-stretch gap-6 pb-16">
        {/* High Exporters Card */}
        <div className="flex-1 min-w-0">
          <ExportersCard
            mode="high"
            attorneys={attorneys}
            loading={loading}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
          />
        </div>

        {/* Low Exporters Card */}
        <div className="flex-1 min-w-0">
          <ExportersCard
            mode="low"
            attorneys={attorneys}
            loading={loading}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
          />
        </div>
      </div>
    </div>
  );
}
